import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import { addDisplayName, addWrapper, addWrapperHOC, flowMax } from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import React, { FC } from 'react'
import { CookiesProvider } from 'react-cookie'
import ReactDOM from 'react-dom'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { addShopperUserContextProvider } from 'shared/src/api'
import { LogOut } from 'shared/src/components/LogOut'
import { addMaintenancePage } from 'shared/src/components/Maintenance'
import 'shared/src/translations/setup'
import { setAnalyticsUserIdOnChange } from 'shared/src/utils/analytics'
import { AuthProvider } from 'shared/src/utils/auth'
import { getEnvVar } from 'shared/src/utils/env'
import { LoadingProvider } from 'shared/src/utils/loading'

import maintenanceImage from 'assets/maintenance.png'
import { Page } from 'components/Page'
import StaticRedirect from 'components/StaticRedirect'
import { SubscribePage } from 'components/SubscribePage'
import { Login } from 'components/auth/Login'
import { Register } from 'components/auth/Register'
import { UpdatePassword } from 'components/auth/UpdatePassword'
import { Edits } from 'components/edits'
import { UserEdit } from 'components/edits/UserEdit'
import { addFeedContextProvider } from 'components/feed/context'
import { Profile } from 'components/profile'
import { mobileOpenDownloadAppBanner } from 'utils/mobileOpenDownloadAppBanner'
import { incrementTimesLaunched } from 'utils/appStorage'
import { addIsTouchActiveContextProvider } from 'utils/globalTouchListeners'
import { addLaunchContextProvider } from 'utils/launchContext'
import { setPageTitle } from 'utils/title'
import { CatchAll } from './components/CatchAll'
import { addBookmarkContextProvider } from './components/bookmarks'
import { Feed } from './components/feed'
import { ProductDetail } from './components/feed/fluid/productDetail'
import { addBannerContextProvider } from './components/banners'
import { addNotificationContextProvider } from './components/notifications'
import { Roundup } from './components/roundup'
import './index.css'
import reportWebVitals from './reportWebVitals'
import { MenuProvider } from './utils/menu'
import { RegisterProvider } from './utils/register'
import { College } from 'components/college'
import { SingleMedia } from 'components/feed/SingleMedia'
import { Referral } from 'components/auth/Referral'

const SENTRY_URL = getEnvVar('SENTRY_URL')

Sentry.init({
  dsn: SENTRY_URL,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 1.0,
  environment: process.env.NODE_ENV,
  enabled: process.env.NODE_ENV !== 'development',
})

const App: FC = flowMax(
  addDisplayName('Shopper'),
  addWrapperHOC((component) => Sentry.withErrorBoundary(component, {})),
  addWrapper((render) => (
    <HelmetProvider>
      <BrowserRouter>
        <CookiesProvider>
          <AuthProvider>
            <LoadingProvider>
              <MenuProvider>
                <RegisterProvider>{render()}</RegisterProvider>
              </MenuProvider>
            </LoadingProvider>
          </AuthProvider>
        </CookiesProvider>
      </BrowserRouter>
    </HelmetProvider>
  )),
  addMaintenancePage(maintenanceImage),
  addBannerContextProvider,
  addNotificationContextProvider,
  addShopperUserContextProvider,
  setAnalyticsUserIdOnChange,
  addBookmarkContextProvider,
  addLaunchContextProvider,
  addIsTouchActiveContextProvider,
  setPageTitle(),
  mobileOpenDownloadAppBanner,
  addFeedContextProvider,
  addEffectOnMount(() => () => {
    incrementTimesLaunched()
  }),
  () => (
    <Routes>
      <Route
        path="/"
        element={
          <Page>
            <Feed />
          </Page>
        }
      />
      <Route
        path="/all"
        element={
          <Page>
            <Feed initialFeedId="featured" />
          </Page>
        }
      />
      <Route
        path="/brands/:brandId"
        element={
          <Page>
            <Feed />
          </Page>
        }
      />
      <Route
        path="/medias/:mediaId"
        element={
          <Page>
            <SingleMedia />
          </Page>
        }
      />
      <Route
        path="/colleges/:shortname"
        element={
          <Page>
            <College />
          </Page>
        }
      />
      <Route
        path="/tags/:tagId"
        element={
          <Page>
            <Feed />
          </Page>
        }
      />
      <Route
        path="/labels/:labelId"
        element={
          <Page>
            <Feed />
          </Page>
        }
      />
      <Route
        path="/following"
        element={
          <Page>
            <Feed initialFeedId="following" />
          </Page>
        }
      />
      <Route
        path="/subscribe"
        element={
          <Page>
            <SubscribePage />
          </Page>
        }
      />
      <Route
        path="/:username"
        element={
          <Page>
            <Profile initialViewType="default" />
          </Page>
        }
      />
      <Route
        path="/:username/posts/rodeo"
        element={
          <Page>
            <Feed />
          </Page>
        }
      />
      <Route
        path="/:username/posts"
        element={
          <Page>
            <Profile initialViewType="posts" />
          </Page>
        }
      />
      <Route path="/:username/edits">
        <Route
          index
          element={
            <Page>
              <Profile initialViewType="edits" />
            </Page>
          }
        />
        <Route
          path=":editId"
          element={
            <Page>
              <UserEdit />
            </Page>
          }
        />
      </Route>
      <Route
        path="/:username/links"
        element={
          <Page>
            <Profile initialViewType="links" />
          </Page>
        }
      />
      <Route
        path="edits/:editId"
        element={
          <Page>
            <Edits />
          </Page>
        }
      />
      <Route
        path="/edits"
        element={
          <Page>
            <Edits />
          </Page>
        }
      />
      <Route
        path="/products/:productId"
        element={
          <Page>
            <ProductDetail />
          </Page>
        }
      />
      <Route
        path="/roundup"
        element={
          <Page>
            <Roundup />
          </Page>
        }
      />
      <Route
        path="/logout"
        element={
          <Page>
            <LogOut />
          </Page>
        }
      />
      <Route
        path="/login"
        element={
          <Page>
            <Login />
          </Page>
        }
      />
      <Route
        path="/register"
        element={
          <Page>
            <Register />
          </Page>
        }
      />
      <Route
        path="/referral/:username"
        element={
          <Page>
            <Referral />
          </Page>
        }
      />
      <Route
        path="/grabTheApp"
        element={
          <Page>
            <Referral grabTheApp />
          </Page>
        }
      />
      <Route
        path="/resetPassword"
        element={
          <Page>
            <UpdatePassword />
          </Page>
        }
      />
      <Route
        path="/terms"
        element={
          <Page>
            <StaticRedirect path="/terms.html" />
          </Page>
        }
      />
      <Route
        path="/privacy"
        element={
          <Page>
            <StaticRedirect path="/privacy.html" />
          </Page>
        }
      />
      <Route
        path="*"
        element={
          <Page>
            <CatchAll />
          </Page>
        }
      />
    </Routes>
  )
)

ReactDOM.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
